import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import Modal from '../../common/Modal';
import BpmFormService from '../../../services/BpmFormService';
import RoleService from '../../../services/RoleService';
import FormBuilderEditor from '../form/FormBuilderEditor';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import dextroModdleDescriptor from './dextro-moddle.json';
import './BpmnModelerEditor.css';

const BpmnModelerEditor = forwardRef(({ xml, onXmlChange }, ref) => {
  const containerRef = useRef(null);
  const modelerRef = useRef(null);
  const lastLoadedXmlRef = useRef(null);
  const initialXmlRef = useRef(xml);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedUserTask, setSelectedUserTask] = useState(null);
  const [taskFormKey, setTaskFormKey] = useState('');
  const [taskCandidateGroups, setTaskCandidateGroups] = useState([]);
  const [formOptions, setFormOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [newFormSaving, setNewFormSaving] = useState(false);
  const [newFormData, setNewFormData] = useState({ formName: '', formKey: '' });
  const [newFormSchema, setNewFormSchema] = useState('');

  const sanitizeFormKey = (name = '') => {
    let result = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    result = result.toLowerCase();
    result = result.replace(/[^a-z0-9]+/g, '_');
    result = result.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    return result;
  };

  const loadCatalogs = async () => {
    try {
      const [formsResponse, rolesResponse] = await Promise.all([
        BpmFormService.getAll(0, 500, {}),
        RoleService.getAll(0, 500, { enabled: true }),
      ]);

      const forms = formsResponse?.content || [];
      const roles = rolesResponse?.content || [];

      setFormOptions(
        forms
          .filter((f) => f?.formKey)
          .map((f) => ({ value: f.formKey, label: f.formName || f.formKey }))
      );

      setGroupOptions(
        roles
          .filter((r) => r?.roleName)
          .map((r) => ({ value: r.roleName, label: r.roleName }))
      );
    } catch (error) {
      setFormOptions([]);
      setGroupOptions([]);
    }
  };

  const applyProcessIdFromModelerKey = (modeler, modelerKey) => {
    if (!modeler || !modelerKey) {
      return;
    }

    const modeling = modeler.get('modeling');
    const canvas = modeler.get('canvas');
    const elementRegistry = modeler.get('elementRegistry');

    let processElement = canvas?.getRootElement?.();
    if (!processElement || processElement.type !== 'bpmn:Process') {
      processElement = elementRegistry?.filter((element) => element?.type === 'bpmn:Process')?.shift();
    }

    if (!processElement) {
      return;
    }

    modeling.updateProperties(processElement, { id: modelerKey });
  };

  useImperativeHandle(ref, () => ({
    exportXmlForSave: async (modelerKey) => {
      const modeler = modelerRef.current;
      if (!modeler) {
        return '';
      }

      applyProcessIdFromModelerKey(modeler, modelerKey);
      const { xml: currentXml } = await modeler.saveXML({ format: true });
      onXmlChange?.(currentXml);
      return currentXml;
    },
  }), [onXmlChange]);

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    if (!isTaskModalOpen) {
      return undefined;
    }

    loadCatalogs();
    const onFocus = () => loadCatalogs();

    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [isTaskModalOpen]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const modeler = new BpmnModeler({
      container: containerRef.current,
      moddleExtensions: {
        dextro: dextroModdleDescriptor,
      },
    });

    modelerRef.current = modeler;

    const loadInitialXml = async () => {
      const initialXml = initialXmlRef.current;
      if (initialXml) {
        await modeler.importXML(initialXml);
        lastLoadedXmlRef.current = initialXml;
      } else {
        await modeler.createDiagram();
        const { xml: createdXml } = await modeler.saveXML({ format: true });
        lastLoadedXmlRef.current = createdXml;
        onXmlChange?.(createdXml);
      }
    };

    loadInitialXml().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error loading BPMN XML', error);
    });

    const onCommandChanged = async () => {
      if (!onXmlChange) {
        return;
      }
      try {
        const { xml: currentXml } = await modeler.saveXML({ format: true });
        onXmlChange(currentXml);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error exporting BPMN XML', error);
      }
    };

    modeler.on('commandStack.changed', onCommandChanged);
    const eventBus = modeler.get('eventBus');
    const handleDoubleClick = (event) => {
      if (!event?.element || event.element.type !== 'bpmn:UserTask') {
        return;
      }
      const bo = event.element.businessObject || event.element;
      const formKey = (bo && typeof bo.get === 'function' && bo.get('dextro:formkey')) || '';
      const candidateGroups =
        (bo && typeof bo.get === 'function' && (bo.get('dextro:group') || bo.get('dextro:candidateGroups'))) || '';

      setSelectedUserTask(event.element);
      setTaskFormKey(formKey);
      setTaskCandidateGroups(
        String(candidateGroups)
          .split(',')
          .map((group) => group.trim())
          .filter((group) => group.length > 0)
      );
      setIsTaskModalOpen(true);

      if (typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
    };
    eventBus.on('element.dblclick', 2000, handleDoubleClick);

    return () => {
      try {
        eventBus.off('element.dblclick', handleDoubleClick);
        modeler.destroy();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error destroying BPMN modeler', error);
      }
    };
  }, [onXmlChange]);

  useEffect(() => {
    const syncXml = async () => {
      const modeler = modelerRef.current;
      if (!modeler) {
        return;
      }

      if (!xml) {
        return;
      }
      const targetXml = xml;
      if (targetXml === lastLoadedXmlRef.current) {
        return;
      }

      await modeler.importXML(targetXml);
      lastLoadedXmlRef.current = targetXml;
    };

    syncXml().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error syncing BPMN XML', error);
    });
  }, [xml]);

  const handleSaveTaskConfig = () => {
    const modeler = modelerRef.current;
    if (!modeler || !selectedUserTask) {
      return;
    }

    const modeling = modeler.get('modeling');
    const candidateGroups = taskCandidateGroups.join(',').trim();
    const formKey = (taskFormKey || '').trim();

    modeling.updateProperties(selectedUserTask, {
      'dextro:group': candidateGroups || undefined,
      'dextro:formkey': formKey || undefined,
      'dextro:candidateGroups': candidateGroups || undefined,
    });

    setIsTaskModalOpen(false);
    setSelectedUserTask(null);
  };

  const handleCreateForm = async () => {
    if (!newFormData.formName?.trim() || !newFormData.formKey?.trim()) {
      return;
    }

    setNewFormSaving(true);
    try {
      const saved = await BpmFormService.create({
        formName: newFormData.formName.trim(),
        formKey: newFormData.formKey.trim(),
        formContent: newFormSchema || '{"type":"default","components":[]}',
        enabled: true,
      });

      await loadCatalogs();
      setTaskFormKey(saved?.formKey || newFormData.formKey.trim());
      setIsCreateFormOpen(false);
      setNewFormSchema('');
      setNewFormData({ formName: '', formKey: '' });
    } finally {
      setNewFormSaving(false);
    }
  };

  return (
    <>
      <div className="bpmn-modeler-canvas" ref={containerRef} />

      <Modal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} title="Configuración de tarea">
        <div className="form-group">
          <label>Clave de formulario</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              className="form-control"
              value={taskFormKey}
              onChange={(e) => setTaskFormKey(e.target.value)}
            >
              <option value="">Seleccionar formulario</option>
              {formOptions.map((formOption) => (
                <option key={formOption.value} value={formOption.value}>
                  {formOption.label}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-default" onClick={() => setIsCreateFormOpen(true)}>
              +
            </button>
          </div>
        </div>
        <div className="form-group">
          <label>Grupos candidatos</label>
          <select
            multiple
            className="form-control"
            value={taskCandidateGroups}
            onChange={(e) =>
              setTaskCandidateGroups(Array.from(e.target.selectedOptions).map((option) => option.value))
            }
            style={{ minHeight: 120 }}
          >
            {groupOptions.map((groupOption) => (
              <option key={groupOption.value} value={groupOption.value}>
                {groupOption.label}
              </option>
            ))}
          </select>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setIsTaskModalOpen(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSaveTaskConfig}>
            Guardar
          </button>
        </div>
      </Modal>

      <Modal isOpen={isCreateFormOpen} onClose={() => setIsCreateFormOpen(false)} title="Nuevo formulario">
        <div className="form-group">
          <label>Nombre formulario</label>
          <input
            type="text"
            className="form-control"
            value={newFormData.formName}
            onChange={(e) => {
              const formName = e.target.value;
              setNewFormData((prev) => ({
                ...prev,
                formName,
                formKey: sanitizeFormKey(formName),
              }));
            }}
          />
        </div>
        <div className="form-group">
          <label>Clave formulario</label>
          <input
            type="text"
            className="form-control"
            value={newFormData.formKey}
            onChange={(e) => setNewFormData((prev) => ({ ...prev, formKey: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <FormBuilderEditor schemaContent={newFormSchema} onSchemaChange={setNewFormSchema} />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setIsCreateFormOpen(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={handleCreateForm} disabled={newFormSaving}>
            {newFormSaving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </Modal>
    </>
  );
});

BpmnModelerEditor.displayName = 'BpmnModelerEditor';

export default BpmnModelerEditor;
